import moment from 'moment-msdate';
import numeral from 'numeral';

const DATE_FORMAT = 'MM/DD/YYYY';
const NO_VALUE = '--';
const NUMBER_FORMAT = '0,0.00';
const PERCENT_FORMAT = '0.00';
const PRICE_FORMAT = '$0,0.00';
const TIME_FORMAT = 'h:mm:ss a';

// set the locales
moment.locale('en-us');
numeral.locale('en');

// set the abbreviations
numeral.locales['en'].abbreviations = {
  thousand: 'K',
  million: 'M',
  billion: 'B',
  trillion: 'T'
};

function sanitizeNumber(val) {
  // This was added to get around a bug in numeral for numbers like 1.176083712526649e-14.
  return val && Number(Number(val).toFixed(11));
}

const isValid = val => {
  return val !== undefined && val !== -32768 && val !== '-32768' && val !== '0000-01-01';
};

const isValidNumber = val => {
  return isValid(val) && !isNaN(val);
};

const isValidText = val => {
  return isValid(val) && val !== null && val !== '';
};

const date = (val, options) => {
  if (!isValidText(val)) {
    return NO_VALUE;
  }

  options = options || {};

  /* To not change this. If getting undefined, fix your data source formatting to match
   * the Supported ISO 8601 strings. https://momentjs.com/docs/#/parsing/string */
  const m = moment(val, moment.ISO_8601);

  return m.format(options.formatString || DATE_FORMAT);
};

const dateTime = (val, options) => {
  if (!isValid(val)) {
    return NO_VALUE;
  }

  options = options || {};

  /* To not change this. If getting undefined, fix your data source formatting to match
   * the Supported ISO 8601 strings. https://momentjs.com/docs/#/parsing/string */
  const m = moment(val, moment.ISO_8601);

  return `${m.format(options.dateFormatString || DATE_FORMAT)} ${m.format(
    options.timeFormatString || TIME_FORMAT
  )}`;
};

const oaDate = (val, options) => {
  if (!isValid(val)) return NO_VALUE;

  options = options || {};

  const momentDate = moment.fromOADate(val);

  return momentDate.format(options.formatString || DATE_FORMAT);
};

const number = (val, options) => {
  if (!isValidNumber(val)) {
    return NO_VALUE;
  }

  val = sanitizeNumber(val);

  options = options || {};

  if (options.showSign && val > 0) {
    options.formatString = `+${options.formatString || NUMBER_FORMAT}`;
  }

  return numeral(val).format(options.formatString || NUMBER_FORMAT);
};

const numberNoZeros = (val, options) => {
  return val === 0 ? NO_VALUE : number(val, options);
};

const percent = (val, options = {}) => {
  if (!isValidNumber(val)) {
    return NO_VALUE;
  }

  const formatString = options.formatString || PERCENT_FORMAT;

  options = Object.assign({}, options, { formatString: `${formatString}%` });
  // NumeralJS has an issue when dealing with a number ends in 5, easy way to fix it.
  return number((Number(val) + 0.000001) / 100, options);
};

const price = (val, options, useRounding = false) => {
  if (!isValidNumber(val)) {
    return NO_VALUE;
  }

  val = sanitizeNumber(val);

  const formatString = options?.formatString ? options.formatString : PRICE_FORMAT;

  return useRounding
    ? numeral(val.toFixed(4)).format(formatString, Math.round)
    : numeral(val.toFixed(4)).format(formatString, Math.floor);
};

const text = val => {
  if (!isValidText(val)) {
    return NO_VALUE;
  }

  return val;
};

const unescapeHTML = html => {
  if (html && typeof html === 'string') {
    const element = document.createElement('div');
    element.innerHTML = html;
    return element.textContent || '';
  }
  return '';
};

const stripHTML = html => {
  if (html && typeof html === 'string') {
    const parser = new DOMParser();
    const document = parser.parseFromString(html, 'text/html');
    return unescapeHTML(document.body.textContent || '');
  }
  return '';
};

const format = {
  NO_VALUE,
  date,
  dateTime,
  isValid,
  isValidNumber,
  isValidText,
  number,
  numberNoZeros,
  oaDate,
  percent,
  price,
  stripHTML,
  text,
  unescapeHTML
};

export default format;
